import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Skeleton } from '@ordo/ui/skeleton'
import { TableCell, TableRow } from '@ordo/ui/table'
import type { ReactNode } from 'react'
import type { Column, FilterOperator, FilterState, SortState } from 'types/data-grid'

/** A column's minimum width when it doesn't set `minWidth`. */
export const DEFAULT_COLUMN_MIN_WIDTH = 120

/**
 * The table's minimum width: the sum of its columns' minimums. Under
 * `table-fixed` with percentage `<col>` widths the table otherwise locks to
 * the screen width, so on a phone Contacts' Phone / Ordo ID / Status columns
 * got under 15px each and their headers ran into each other (2026-10-18).
 */
export function tableMinWidth(columns: Pick<Column<unknown>, 'minWidth'>[]): number {
  return columns.reduce((sum, col) => sum + (col.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH), 0)
}

export function SortIndicator({ column, sort }: { column: string; sort: SortState | null }) {
  const active = sort?.column === column
  if (!active) {
    return <span className="ml-1 shrink-0 text-[11px] text-muted-foreground">{'↕'}</span>
  }
  return <span className="ml-1 shrink-0 text-[11px]">{sort.direction === 'asc' ? '↑' : '↓'}</span>
}

/**
 * A header's label and sort arrow, clipped to the cell: the label truncates,
 * the arrow stays visible, and nothing spills into the next column's header.
 */
export function HeaderLabel({
  header,
  column,
  sortable,
  sort,
}: {
  header: string
  column: string
  sortable?: boolean
  sort: SortState | null
}) {
  return (
    <span className="inline-flex max-w-full items-center align-middle">
      <span className="truncate">{header}</span>
      {sortable && <SortIndicator column={column} sort={sort} />}
    </span>
  )
}

/** Placeholder rows shaped like the grid while it loads, never a "Loading..." line. */
export function SkeletonRows({ columns, rows = 5 }: { columns: Pick<Column<unknown>, 'key'>[]; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i} className="hover:bg-transparent" data-testid="grid-skeleton-row">
          {columns.map((col) => (
            <TableCell key={col.key} className="px-3.5 py-2.5">
              <Skeleton className="h-4 w-3/4" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

export function PaginationButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void
  disabled: boolean
  children: ReactNode
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className="h-10 px-3 text-[13px] md:h-7 md:px-2.5">
      {children}
    </Button>
  )
}

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  like: 'Like',
  present: 'Present',
  empty: 'Empty',
}

export function ColumnFilter({
  header,
  filter,
  onUpdate,
  onClear,
}: {
  header: string
  filter: FilterState | undefined
  onUpdate: (operator: FilterOperator, value: string) => void
  onClear: () => void
}) {
  const operator = filter?.operator ?? 'like'
  const value = filter?.value ?? ''
  const isActive = filter != null

  function handleOperatorChange(newOp: string) {
    if (newOp === '_clear') {
      onClear()
    } else {
      onUpdate(newOp as FilterOperator, newOp === 'like' ? value : '')
    }
  }

  return (
    <div className="flex min-w-0 items-center gap-1">
      <Select value={isActive ? operator : '_clear'} onValueChange={handleOperatorChange}>
        <SelectTrigger className="h-7 w-auto min-w-0 max-w-full border-0 bg-transparent px-0 text-[13px] shadow-none focus:ring-0 focus:ring-offset-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_clear" className="text-muted-foreground">
            No filter
          </SelectItem>
          {(Object.keys(OPERATOR_LABELS) as FilterOperator[]).map((op) => (
            <SelectItem key={op} value={op}>
              {OPERATOR_LABELS[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isActive && operator === 'like' && (
        <Input
          type="text"
          placeholder={`${header.toLowerCase()}...`}
          value={value}
          onChange={(e) => onUpdate('like', e.target.value)}
          className="h-7 min-w-0 flex-1 text-[13px]"
        />
      )}
    </div>
  )
}
